import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { businessInfo } from '../mockData';
import { siteConfig, rates, fixedQuoteLine, afterHoursRateLine, servicePages } from '../content/siteContent';
import usePageMeta from '../hooks/usePageMeta';
import NotFound from '../pages/NotFound';

const GatedBanner = () => (
  <div className="bg-amber-50 border-b border-amber-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-start gap-3">
      <Clock size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
      <p className="text-sm text-amber-900">
        <span className="font-semibold">Launching soon.</span> This service starts once our
        qualified locksmith joins the team. In the meantime we can help with lock repairs,
        replacements and security upgrades —{' '}
        <a href={`tel:${businessInfo.phoneTel}`} className="underline font-medium">
          call {businessInfo.phone}
        </a>.
      </p>
    </div>
  </div>
);

const RateCard = ({ priceNote }) => (
  <Card className="border-red-200 bg-red-50">
    <CardContent className="p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Simple, upfront pricing</h3>
      <ul className="space-y-2 text-gray-700">
        <li className="flex justify-between gap-4 border-b border-red-100 pb-2">
          <span>Call-out — includes travel + first 15 minutes on site</span>
          <span className="font-bold whitespace-nowrap">${rates.callout} + GST</span>
        </li>
        <li className="flex justify-between gap-4 border-b border-red-100 pb-2">
          <span>Each additional 15 minutes</span>
          <span className="font-bold whitespace-nowrap">${rates.block15} + GST</span>
        </li>
      </ul>
      {priceNote && <p className="text-sm text-gray-700 mt-3">{priceNote}</p>}
      <p className="text-sm text-gray-700 mt-3">{afterHoursRateLine}</p>
      <p className="text-sm font-semibold text-red-800 mt-3">{fixedQuoteLine}</p>
    </CardContent>
  </Card>
);

export const ServicePageBody = ({ cfg }) => {
  const gatedNow = cfg.gated && !siteConfig.locksmithLive;
  usePageMeta({ title: cfg.metaTitle, description: cfg.metaDescription, noindex: gatedNow });

  const primaryCta =
    cfg.primaryCta?.type === 'quote' ? (
      <Link to="/contact">
        <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
          Request a quote
          <ArrowRight className="ml-2" size={20} />
        </Button>
      </Link>
    ) : (
      <a href={`tel:${businessInfo.phoneTel}`}>
        <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
          <Phone className="mr-2" size={20} />
          Call {businessInfo.phone}
        </Button>
      </a>
    );

  // Supports a single video (cfg.video/videoCaption) or several (cfg.videos[]).
  const videos = cfg.videos || (cfg.video ? [{ src: cfg.video, caption: cfg.videoCaption }] : []);

  return (
    <div className="min-h-screen">
      {gatedNow && <GatedBanner />}

      {/* Hero — page photo underlays the dark panel */}
      <section className="relative bg-gray-950 py-16 sm:py-20 text-white overflow-hidden">
        {cfg.image && cfg.heroImageRight ? (
          <div className="absolute inset-y-0 right-0 w-full md:w-3/5" aria-hidden="true">
            <img src={cfg.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/50 to-gray-950/5"></div>
          </div>
        ) : cfg.image ? (
          <div className="absolute inset-0" aria-hidden="true">
            <img src={cfg.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-950 from-25% via-gray-950/75 via-55% to-gray-950/5"></div>
          </div>
        ) : null}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-5">{cfg.title}</h1>
            <p className="text-xl text-blue-100 mb-8">{cfg.heroSub}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              {primaryCta}
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold text-lg px-8">
                  Get in touch
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-300 mt-5">
              {siteConfig.hoursLine} · Mobile service — we come to you
            </p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-2">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">{cfg.intro}</p>
              {videos.length > 0 && (
                <div className={`mb-10 ${videos.length > 1 ? 'grid sm:grid-cols-2 gap-4' : ''}`}>
                  {videos.map((v) => (
                    <figure key={v.src} className="m-0">
                      <video
                        controls
                        playsInline
                        preload="metadata"
                        className={
                          videos.length > 1
                            ? 'w-full h-[400px] object-contain bg-gray-950 rounded-2xl shadow-lg'
                            : 'rounded-2xl shadow-lg mx-auto max-w-full max-h-[70vh] bg-gray-900'
                        }
                        src={v.src}
                      />
                      {v.caption && (
                        <figcaption className="text-sm text-gray-500 mt-3 text-center">{v.caption}</figcaption>
                      )}
                    </figure>
                  ))}
                </div>
              )}
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-5">{cfg.scopeTitle}</h2>
              <ul className="space-y-3 mb-8">
                {cfg.scope.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {cfg.details?.map((d) => (
                <div key={d.h2} className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">{d.h2}</h2>
                  <p className="text-gray-700 leading-relaxed">{d.text}</p>
                </div>
              ))}
              {cfg.faqs?.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Common questions</h2>
                  <dl className="space-y-4">
                    {cfg.faqs.map((f) => (
                      <div key={f.q} className="border-l-4 border-red-200 pl-4">
                        <dt className="font-semibold text-gray-900">{f.q}</dt>
                        <dd className="text-gray-700 mt-1">{f.a}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {cfg.crossLinks?.length > 0 && (
                <div className="flex flex-wrap gap-3 pt-2">
                  {cfg.crossLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-sm border border-blue-200 rounded-full px-4 py-2 hover:bg-blue-50 transition-colors"
                    >
                      {l.label} <ArrowRight size={14} />
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-6">
              {cfg.showRates && <RateCard priceNote={cfg.priceNote} />}
              <Card className="border-gray-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Why Shore Doors Locksmiths</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><CheckCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={15} /><span>The install team trusted by Auckland's security companies</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={15} /><span>12-month workmanship guarantee</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={15} /><span>Work meets NZ building codes and fire safety requirements</span></li>
                    <li className="flex items-start gap-2"><CheckCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={15} /><span>Mobile across {siteConfig.commercialArea.toLowerCase()}</span></li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Ready when you are</h2>
          <p className="text-lg text-gray-300 mb-7">{fixedQuoteLine}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">{primaryCta}</div>
        </div>
      </section>
    </div>
  );
};

// Route component: /residential/:slug and /commercial/:slug
const ServicePage = ({ section }) => {
  const { slug } = useParams();
  const cfg = servicePages[section]?.[slug];
  if (!cfg) return <NotFound />;
  return <ServicePageBody cfg={cfg} key={`${section}/${slug}`} />;
};

export default ServicePage;
